const HTML_TAG_REGEXP = /<[^>]*>/g;
// &nbsp; &amp; &#12354; &#x3042; などの文字参照
const HTML_ENTITY_REGEXP = /&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi;
const LINE_BREAK_REGEXP = /\r\n|\r|\n/g;
const CRLF = '\r\n';
const LF = '\n';
// 文字参照1つを見た目上の1文字として数えるための置換文字
const ENTITY_PLACEHOLDER = '_';

// Jodit では Enter で段落（<p>）が、Shift+Enter で <br> が改行になる
const PARAGRAPH_BOUNDARY_REGEXP = /<\/p><p(?:\s[^>]*)?>/gi;
const BR_TAG_REGEXP = /<br[^>]*>/gi;
// 段落の末尾の <br> は改行として表示されない（Jodit が空行や行末に置くもの）
const BR_AT_PARAGRAPH_END_REGEXP = /<br[^>]*>(?=<\/p>)/gi;
const TRAILING_LINE_BREAKS_REGEXP = /\n+$/;

// サロゲートペア（絵文字等）を1文字として数える。DB の varchar と同じ単位で数える
const countCodePoints = (text: string): number => Array.from(text).length;

// Intl.Segmenter が無い古いブラウザ（Firefox 124 以前等）では undefined
const graphemeSegmenter = typeof Intl.Segmenter === 'undefined'
  ? undefined
  : new Intl.Segmenter('ja', { granularity: 'grapheme' });

// 見た目の1文字（組み合わせ絵文字 👨‍👩‍👧 等も1文字）ごとに分割する
const splitGraphemes = (text: string): string[] => {
  // Intl.Segmenter が無い古いブラウザでは、コードポイント単位で分割する。CRLF は分割しない
  if (!graphemeSegmenter) {
    return text.match(/\r\n|[\s\S]/gu) ?? [];
  }

  return Array.from(graphemeSegmenter.segment(text), ({ segment }) => segment);
};

// 保存される HTML 文字列の文字数を返す。空文字は0
// 送信時に改行は CRLF になるため、DB に保存される長さと一致させるよう改行は2文字で数える
export function countRichTextTokens(html: string): number {
  return countCodePoints(html.replace(LINE_BREAK_REGEXP, CRLF));
}

// 見た目上の改行（段落の区切り・<br>）を LF に置き換え、それ以外のタグを除く
// HTML ソース上の改行文字は表示されないため除く。末尾の改行は除く
const toVisibleText = (html: string): string => html
  .replace(LINE_BREAK_REGEXP, '')
  .replace(BR_AT_PARAGRAPH_END_REGEXP, '')
  .replace(PARAGRAPH_BOUNDARY_REGEXP, LF)
  .replace(BR_TAG_REGEXP, LF)
  .replace(HTML_TAG_REGEXP, '')
  .replace(TRAILING_LINE_BREAKS_REGEXP, '');

// 見た目上の文字数を返す。見た目上の改行と文字参照は1文字として数える。空文字は0
export function countRichTextChars(html: string): number {
  const visibleText = toVisibleText(html).replace(HTML_ENTITY_REGEXP, ENTITY_PLACEHOLDER);
  return splitGraphemes(visibleText).length;
}

// トークン数が上限を超えていれば true。上限ちょうどは false
export function isRichTextTokenCountOver(html: string, maxTokenCount: number): boolean {
  return countRichTextTokens(html) > maxTokenCount;
}

// トークン数が上限に収まるよう末尾を切り詰める。見た目の1文字の途中では切らない（Intl.Segmenter が無い環境ではコードポイント単位）
export function truncateRichTextTokens(html: string, maxTokenCount: number): string {
  let truncated = '';
  let tokenCount = 0;
  for (const grapheme of splitGraphemes(html)) {
    tokenCount += countRichTextTokens(grapheme);
    if (tokenCount > maxTokenCount) break;

    truncated += grapheme;
  }
  return truncated;
}
